import Ajv2020 from "ajv/dist/2020";
import addFormats from "ajv-formats";
import schema from "./schema.json";
import samples from "./samples.json";

export const VERSION = "2026-04-17";
export const SOURCE = "https://github.com/agentic-commerce-protocol/agentic-commerce-protocol/blob/7fdd78df677a94dce04c770644b0fbbb1401272b/spec/2026-04-17/json-schema/schema.agentic_checkout.json";
export const KINDS = ["create", "update", "complete", "response"] as const;
export type Kind = typeof KINDS[number];
export type Issue = { path: string; rule: string; detail: string };
export type Result = { status: "valid" | "invalid" | "syntax" | "limit" | "failed"; issues: Issue[]; truncated?: boolean };
const names = { create: "CheckoutSessionCreateRequest", update: "CheckoutSessionUpdateRequest", complete: "CheckoutSessionCompleteRequest", response: "CheckoutSession" };
const ajv = new Ajv2020({ strict: false, allErrors: true, validateFormats: true });
addFormats(ajv);
ajv.addSchema(schema, "acp");
const validators = Object.fromEntries(KINDS.map((kind) => [kind, ajv.compile({ $ref: `acp#/$defs/${names[kind]}` })]));

export function example(kind: Kind, invalid = false): string {
  const value = JSON.parse(JSON.stringify(samples[kind]));
  if (invalid) delete value[kind === "create" || kind === "response" ? "currency" : kind === "complete" ? "payment_data" : "fulfillment_details"];
  // Update accepts an empty object; an invalid field demonstrates the actual constraint instead.
  if (invalid && kind === "update") value.fulfillment_details = "invalid";
  return JSON.stringify(value, null, 2);
}

export function validate(text: string, kind: Kind): Result {
  if (new TextEncoder().encode(text).length > 1_000_000) return { status: "limit", issues: [] };
  let data: unknown;
  try { data = JSON.parse(text); } catch { return { status: "syntax", issues: [] }; }
  const stack: { value: unknown; depth: number }[] = [{ value: data, depth: 0 }];
  let nodes = 0;
  while (stack.length) {
    const { value, depth } = stack.pop()!;
    if (++nodes > 20_000 || depth > 40) return { status: "limit", issues: [] };
    if (value && typeof value === "object") Object.values(value).forEach((child) => stack.push({ value: child, depth: depth + 1 }));
  }
  try {
    const check = validators[kind];
    if (check(data)) return { status: "valid", issues: [] };
    const errors = check.errors ?? [];
    return { status: "invalid", truncated: errors.length > 50, issues: errors.slice(0, 50).map((error) => ({
      path: error.instancePath || "/", rule: error.keyword,
      detail: String(error.params.missingProperty ?? error.params.additionalProperty ?? error.params.type ?? error.params.format ?? ""),
    })) };
  } catch { return { status: "failed", issues: [] }; }
}
